<template>
	<div>
		<base-header class="pb-6 bg-light">
			<b-row align-v="center" class="py-4">
				<b-col cols="12" lg="6">
					<h6 class="h2 d-inline-block mb-0 text-dark">Address Manager</h6>
				</b-col>
				<b-col cols="12" lg="6" class="text-right">
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</b-col>
			</b-row>
		</base-header>
		<b-card>
			<b-form @submit.prevent="getAddress" class="form-horizontal">
				<b-row class="align-items-start">
					<b-col cols="auto">
						<label for="imsi-input" class="font-weight-600">IMSI</label>
					</b-col>
					<b-col>
						<b-input
							id="imsi-input"
							v-model="imsi"
							size="sm"
							type="number"
							placeholder="Enter IMSI"
							class="mb-3"
							:state="imsiState"
						>
						</b-input>
						<b-form-invalid-feedback :state="imsiState">
							IMSI must be 15 digits long
						</b-form-invalid-feedback>
					</b-col>
					<b-col cols="auto">
						<label for="msisdn-input" class="font-weight-600">MSISDN</label>
					</b-col>
					<b-col>
						<b-input
							id="msisdn-input"
							v-model="msisdn"
							size="sm"
							type="number"
							placeholder="Enter MSISDN"
							class="mb-3"
							:state="msisdnState"
						>
						</b-input>
						<b-form-invalid-feedback :state="msisdnState">
							MSISDN must be 11 digits long
						</b-form-invalid-feedback>
					</b-col>
					<b-col>
						<b-button
              data-cy="address-manager-search-btn" 
              variant="primary" 
              type="submit" 
              size="sm" 
              :disabled="submitDisabled"
            >
							<i class="fas fa-search"></i>
							Search
						</b-button>
					</b-col>
				</b-row>
			</b-form>
		</b-card>
		<b-card>
			<b-card-body>
				<b-row>
					<b-col cols="12">
						<b-button
              v-if="!isReadOnly"
							class="mb-3 float-right"
							variant="success"
							size="sm"
							@click="$bvModal.show('add-address')"
						>
							<i class="fas fa-plus"></i>
							Add Address
						</b-button>
					</b-col>
				</b-row>
				<b-row class="align-items-end">
					<b-col cols="12" xl="8" lg="7" md="7" sm="6">
						<b-form-group
							label=""
							label-for="per-page-select"
							label-cols-sm="0"
							label-cols-md="0"
							label-cols-lg="0"
							label-align-sm="center"
							label-size="sm"
							class="per-page-select"
						>
							<label for="per-page-select" class="mr-2 text-sm mt-1 mr-4">Show</label>
							<b-form-select
								id="per-page-select"
								class="diameter-report-select"
								v-model="perPage"
								:options="pageOptions"
								size="sm"
								v-on:input="getTotalRows"
							></b-form-select>
						</b-form-group>
					</b-col>
					<b-col cols="12" xl="4" lg="5" md="5" sm="6" class="customer-table-select">
						<b-form-group
							label=""
							label-for="filter-input"
							label-align-sm="right"
							label-size="sm "
							class="per-page-select"
						>
							<label for="filter-input" class="text-sm mr-2 mt-1">Search:</label>
							<b-input-group size="sm">
								<b-form-input id="filter-input2" v-model="filter"></b-form-input>
							</b-input-group>
						</b-form-group>
					</b-col>
				</b-row>

				<b-table
					:items="items"
					:fields="fields"
					:current-page="currentPage"
					:per-page="perPage"
					:filter="filter"
					:sort-by.sync="sortBy"
					:sort-desc.sync="sortDesc"
					:sort-direction.sync="sortDirection"
					class="text-center primary-table"
					hover
					responsive
					show-empty
					small
					striped
					@filtered="onFiltered"
				>
					<template #empty="scope">
						<div v-if="loadingStatus" class="text-center">
							<b-icon icon="three-dots" animation="cylon" font-scale="4" />
						</div>
						<div v-else class="text-center">
							<b>{{ scope.emptyText }}</b>
						</div>
					</template>

					<template #cell(actions)="data">
						<i 
              v-if="!isReadOnly"
							class="fas fa-edit text-success mr-3 cursor-pointer"
							title="Edit Address" 
							@click="updateAddress(data.item)"
						></i>
						<i 
              v-if="!isReadOnly"
							class="fas fa-trash-alt text-danger cursor-pointer"
							title="Delete Address" 
							@click="
								selectedAddress = data.item;
								$bvModal.show('delete-address');
							"
						></i>
					</template>
				</b-table>
				<b-row class="mt-4" v-if="items.length > 0">
					<b-col> Showing {{ startPage() }} - {{ lastPage() }} of {{ totalRows }} entries </b-col>
					<b-col xl="auto" md="auto" sm="auto ">
						<b-pagination
							class="flex-fill-custom pr-4"
							v-model="currentPage"
							:total-rows="totalRows"
							:per-page="perPage"
							size="sm"
						></b-pagination>
					</b-col>
				</b-row>
			</b-card-body>
		</b-card>

		<!-- Modals -->
		<add-address-modal
			id="add-address"
		></add-address-modal>

		<edit-address-modal
			id="edit-address"
			:selectedAddress="selectedAddress"
		></edit-address-modal>

		<b-modal
			id="delete-address"
			title="Delete Address"
			size="sm"
			centered
		>
			<p>Are you sure you want to delete this address?</p>
			<p class="font-weight-600 pb-0 mb-0 ml-4">
				{{ selectedAddress.address2 }}
			</p>
			<p class="font-weight-600 ml-4">
				{{ selectedAddress.city }}, {{ selectedAddress.state }} {{ selectedAddress.zipcode }}
			</p>
			<template #modal-footer>
				<div class="w-100">
				<b-button
					variant="danger"
					class="float-right ml-3"
					:disabled="deleteDisabled"
					@click="deleteAddress"
				>
					<i v-if="deleteLoading" class="fas fa-spinner fa-spin"> </i>
					Delete
				</b-button>
				<b-button
					variant="primary"
					class="float-right"
					@click="$bvModal.hide('delete-address')"
				>
					Cancel
				</b-button>
				</div>
			</template>
		</b-modal>
	</div>
</template>

<script>
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import AddAddressModal from "./AddAddressModal";
import EditAddressModal from "./EditAddressModal";
import Toast from "../../Components/Toast";
import entitlementsService from "@/common/api-services/entitlements-dashboard-service";
import pagination from "../../../common/format/pagination";
import initService from "../../../common/api-services/init-service";
import moment from "moment";

export default {
	name: "AddressManager",
	components: {
		RouteBreadCrumb,
		AddAddressModal,
		EditAddressModal,
		Toast,
	},
	mounted() {
		document.title = "Entitlements - Address Manager";
		this.$root.$on("refresh-search", (params) => {
			this.imsi = params.imsi
			this.msisdn = params.msisdn
			this.getAddress()
		})

    if (this.noAccess) {
      this.$router.push('/restricted').catch(() => {});
    } else {
      //set the roles [isReadOnly, isEditor, isAdministrator, isPortalAdmin]
      this.isReadOnly = initService.getUserRoles().includes("ENT_READ");
      this.isEditor = initService.getUserRoles().includes("ENTITLEMENTS_EDIT");
      this.isAdministrator =
        initService.getUserRoles().includes("PORTAL_ADMIN") ||
        initService.getUserRoles().includes("CARRIER_ADMIN") ||
        initService.getUserRoles().includes("ENTITLEMENTS_ADMIN");
      this.isPortalAdmin = initService.getPortalAdminRole();

      if (this.isReadOnly) {
        this.fields = this.fields.filter((field) => field.key !== "actions");
      }
    }
	},
	data() {
		return {
			imsi: "",
			msisdn: "",

			address: {
				streetAddress: "",
				streetAddress2: "",
				city: "",
				state: "",
				zipCode: "",
			},
			selectedAddress: {},
			message: "",

			items: [],
			fields: [
				{key: "actions", label: ""},
				{key: "address1", label: "Street Address 1", sortable: true},
				{key: "address2", label: "Street Address 2", sortable: true},
				{key: "city", label: "City", sortable: true},
				{key: "state", label: "State", sortable: true},
				{key: "zipcode", label: "Zip Code", sortable: true},
				{key: "dateCreated", label: "Created", sortable: true},
				{key: "dateExpires", label: "Expires", sortable: true},
			],
			currentPage: 1,
			perPage: 10,
			pageOptions: [10, 25, 50, 100],
			totalRows: 0,
			sortBy: "group",
			sortDesc: false,
			sortDirection: "asc",
			filter: null,
			customer: "",
			loadingStatus: false,
			deleteLoading: false,
			deleteDisabled: false,

      isReadOnly: false,
			isEditor: false,
			isAdministrator: false,
			isPortalAdmin: false,
		};
	},
	computed: {
		submitDisabled() {
			return this.imsiState && this.msisdnState ? false : true;
		},
		imsiState() {
			return this.imsi.length === 15 ? true : false;
		},
		msisdnState() {
			return this.msisdn.length === 11 ? true : false;
		},
		noAccess() {
			// Determine access based on menu link configuration
			let userRoles = initService.getUserRoles();
			let link = this.$store.getters["getLinkByLink"](this.$router.currentRoute.path);

			if (!link) {
				return true;
			} else {
				userRoles = userRoles.filter(role => link.roles.includes(role.toUpperCase()));
				return userRoles.length === 0;
			}
		},
	},
	methods: {
		getAddress() {
			this.loadingStatus = true;
			this.items = [];
			this.totalRows = 0;
			entitlementsService
				.GetAddress(this.imsi, this.msisdn)
				.then(res => {
					// console.log("GetAddress:", res);
					if (typeof res === 'string') {
						throw new Error(res)
					}
          
          res.dateCreated = moment(res.dateCreated).format("MM/DD/YYYY");
          res.dateExpires = moment(res.dateExpires).format("MM/DD/YYYY");
          
					this.items.push({...res})
					this.totalRows = this.items.length;
				})
				.catch(err => {
					err = err.toString();
					if (!err.toLowerCase().includes("null response")) {
						console.error("GetAddress:", err);
						this.showToast("error", err)
					}
				})
				.finally(() => {
					this.loadingStatus = false;
				});
		},

		updateAddress(item) {
			// console.log(item);
			this.selectedAddress = item;
			this.$root.$emit("EditAddressState", item)
			this.$bvModal.show("edit-address");
		},

		deleteAddress() {
			// console.log("deleteAddress:", this.selectedAddress);
			this.deleteLoading = true;
			this.deleteDisabled = true;

			entitlementsService
				.DeleteAddress(this.selectedAddress.imsi, this.selectedAddress.msisdn)
				.then(res => {
					// console.log("DeleteAddress:", res);
					this.$bvModal.hide("delete-address");
					this.showToast("success", "Address deleted successfully");
					this.getAddress();
				})
				.catch(err => {
					console.error("DeleteAddress:", err);
					this.showToast("Error deleting address", "danger");
				})
				.finally(() => {
					this.deleteLoading = false;
					this.deleteDisabled = false;
				});
		},

        showToast(type, message, subtitle, title) {
			switch (type) {
				case "error":
					this.$toast.error({
						component: Toast,
						props: {
							subtitle: subtitle,
							title: title,
							description: message,
						}, 
						duration: 4000
					});
				break;
				case "success":
					this.$toast.success({
						component: Toast,
						props: {
							subtitle: subtitle,
							title: title,
							description: message,
						}, 
						duration: 4000
					});
				break;
				case "warning":
					this.$toast.warning({
						component: Toast,
						props: {
							subtitle: subtitle,
							title: title,
							description: message,
						}, 
						duration: 4000
					});
				break;
				default:
					this.$toast({
						component: Toast,
						props: {
							subtitle: subtitle,
							title: title,
							description: message,
						}, 
						duration: 4000
					})
			}
		},

		getTotalRows() {
			if (this.perPage > this.totalRows) {
				this.fromPage = this.totalRows;
			} else {
				this.fromPage = this.perPage;
			}
		},

		startPage() {
			return pagination.startPageFunc(this.currentPage, this.perPage);
		},

		lastPage() {
			return pagination.lastPageFunc(this.currentPage, this.perPage, this.totalRows);
		},

		onFiltered(filteredItems) {
			this.totalRows = filteredItems.length;
			this.currentPage = 1;
		},
	},
};
</script>
